import { useState } from 'react';

import { useCreateMcpConnector } from '../hooks/use-mcp-connectors';

import { McpConnectorStep1Identity } from './McpConnectorStep1Identity';
import { McpConnectorStep2Connection } from './McpConnectorStep2Connection';
import { McpConnectorStep3Review } from './McpConnectorStep3Review';

import type { CreateMcpConnectorInput } from '../types';

export interface McpConnectorWizardProps {
  open: boolean;
  onClose: () => void;
}

type StepNumber = 1 | 2 | 3;

/**
 * Sprint A4 S1 PR 3 — Tasks 7+8+9. Wizard canonical de 3 passos.
 *
 *   Step 1 Identidade  → displayName + slug (auto) + namespace (default = slug)
 *   Step 2 Conexão     → serverUrl + auth method (lock api-key v1) + apiKey
 *   Step 3 Revisão     → resumo + fingerprint da key + submit
 *
 * Submit dispara `useCreateMcpConnector` (POST /v1/admin/integrations/
 * mcp-connectors) — backend cria conector em status `pending-handshake` +
 * dispara handshake fire-and-forget. WS event `mcp.handshake-completed`
 * (Task 10) atualiza status final (active/failed).
 */
export function McpConnectorWizard({ open, onClose }: McpConnectorWizardProps): JSX.Element | null {
  const [step, setStep] = useState<StepNumber>(1);
  const [formState, setFormState] = useState<Partial<CreateMcpConnectorInput>>({});
  const createMutation = useCreateMcpConnector();
  const [submitError, setSubmitError] = useState<string | null>(null);

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
      <div className="w-full max-w-2xl rounded-lg bg-surface p-6">
        <div className="mb-6 flex items-center justify-between">
          <h2 className="text-lg font-semibold text-text-primary">Adicionar conector MCP</h2>
          <button
            type="button"
            onClick={onClose}
            disabled={createMutation.isPending}
            aria-label="Fechar"
            className="text-text-secondary hover:text-text-primary disabled:opacity-50"
          >
            ✕
          </button>
        </div>

        <div className="mb-6 flex gap-2">
          <StepBadge n={1} label="Identidade" active={step === 1} done={step > 1} />
          <StepBadge n={2} label="Conexão" active={step === 2} done={step > 2} />
          <StepBadge n={3} label="Revisão" active={step === 3} done={false} />
        </div>

        {step === 1 && (
          <McpConnectorStep1Identity
            value={formState}
            onNext={(data) => {
              setFormState({ ...formState, ...data });
              setStep(2);
            }}
          />
        )}
        {step === 2 && (
          <McpConnectorStep2Connection
            value={formState}
            onBack={() => setStep(1)}
            onNext={(data) => {
              setFormState({ ...formState, ...data });
              setStep(3);
            }}
          />
        )}
        {step === 3 && (
          <>
            <McpConnectorStep3Review
              value={formState as CreateMcpConnectorInput}
              isSubmitting={createMutation.isPending}
              onBack={() => setStep(2)}
              onSubmit={() => {
                setSubmitError(null);
                createMutation.mutate(formState as CreateMcpConnectorInput, {
                  onSuccess: () => {
                    onClose();
                  },
                  onError: (err: Error) => {
                    setSubmitError(err.message);
                  },
                });
              }}
            />
            {submitError !== null && (
              <div className="mt-4 rounded bg-red-50 p-3 text-xs text-red-900">
                <strong>Erro ao salvar:</strong> {submitError}
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}

function StepBadge({
  n,
  label,
  active,
  done,
}: {
  n: number;
  label: string;
  active: boolean;
  done: boolean;
}): JSX.Element {
  return (
    <div
      className={`flex flex-1 items-center gap-2 rounded px-3 py-2 text-xs ${
        active
          ? 'bg-accent text-white'
          : done
            ? 'bg-green-100 text-green-700'
            : 'bg-bg-muted text-text-secondary'
      }`}
    >
      <span className="font-bold">{done ? '✓' : n}</span>
      <span>{label}</span>
    </div>
  );
}
